import { type JSX, useState } from 'react';

import { SELF_PAY_PAYER_ID, payerIdForGroup } from '../../config/supported-payers.js';
import type { EligibilityCheckInput } from '../../lib/ffs-api.js';

import { PayerSelect } from './payer-select.js';

const DOB_RE = /^\d{4}-\d{2}-\d{2}$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * US phone reduced to 10 digits: punctuation stripped, a leading country-code `1` dropped.
 * Mirrors `normalizePatientPhone` in @nrx/carebetty (which the server re-applies, idempotently) —
 * duplicated rather than imported because that package is server-side and off-limits to the app.
 */
export function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/** A US phone we can hand to CareBetty and the Zoom prefill: exactly 10 digits once normalized. */
export function isValidPhone(value: string): boolean {
  return normalizePhone(value).length === 10;
}

/**
 * The service's limits (`services/ffs-signup/src/interface/http/member-identity.schema.ts`). The
 * input stops at them, so a member can never submit a value the server rejects for its length.
 */
const MAX_NAME_LENGTH = 100;
const MAX_MEMBER_ID_LENGTH = 80;

/** Valid, real calendar ISO date (YYYY-MM-DD), not in the future. */
export function isValidDob(dob: string): boolean {
  if (!DOB_RE.test(dob)) return false;
  const d = new Date(`${dob}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  // Reject calendar-invalid dates (JS Date rolls Feb 30 → Mar 2, etc.).
  if (d.toISOString().slice(0, 10) !== dob) return false;
  // Not in the future (compare UTC calendar dates).
  return dob <= new Date().toISOString().slice(0, 10);
}

export function EligibilityForm({
  onSubmit,
  submitting = false,
}: {
  onSubmit: (input: EligibilityCheckInput) => void;
  submitting?: boolean;
}): JSX.Element {
  const [groupId, setGroupId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const payerId = payerIdForGroup(groupId) ?? '';
  const selfPay = payerId === SELF_PAY_PAYER_ID;

  const dobValid = dateOfBirth === '' || isValidDob(dateOfBirth);
  const emailValid = email === '' || EMAIL_RE.test(email);
  const phoneValid = phone === '' || isValidPhone(phone);
  const valid =
    groupId !== '' &&
    // Gate on the DERIVED payer too, not just the selection. If the option list and
    // payerIdForGroup ever drift, a selectable group the lookup cannot resolve would otherwise
    // submit an empty payerId to the server.
    payerId !== '' &&
    (selfPay || memberId.trim() !== '') &&
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    isValidDob(dateOfBirth) &&
    EMAIL_RE.test(email) &&
    isValidPhone(phone);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!valid || submitting) return;
    onSubmit({
      payerId,
      groupId,
      ...(selfPay ? {} : { memberId: memberId.trim() }),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth,
      email: email.trim(),
      phone: normalizePhone(phone),
    });
  }

  const field =
    'rounded-xl border border-[var(--color-field-border)] bg-white px-4 py-2.5 text-[var(--ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]';
  const labelText = 'text-sm font-medium text-[var(--ink)]';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <label className="flex flex-col gap-1.5">
        <span className={labelText}>Health plan</span>
        <PayerSelect id="payer" value={groupId} onChange={setGroupId} />
      </label>
      {!selfPay && (
        <label className="flex flex-col gap-1.5">
          <span className={labelText}>Member ID</span>
          <input
            className={field}
            value={memberId}
            maxLength={MAX_MEMBER_ID_LENGTH}
            onChange={(e) => setMemberId(e.target.value)}
          />
        </label>
      )}
      <label className="flex flex-col gap-1.5">
        <span className={labelText}>First name</span>
        <input
          className={field}
          value={firstName}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setFirstName(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={labelText}>Last name</span>
        <input
          className={field}
          value={lastName}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setLastName(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={labelText}>Email</span>
        <input
          type="email"
          className={field}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={!emailValid}
        />
        {!emailValid && (
          <span className="text-sm text-[var(--coral-dark)]">Enter a valid email address.</span>
        )}
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={labelText}>Phone</span>
        <input
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          className={field}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          aria-invalid={!phoneValid}
        />
        {!phoneValid && (
          <span className="text-sm text-[var(--coral-dark)]">Enter a valid phone number.</span>
        )}
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={labelText}>Date of birth</span>
        <input
          type="date"
          className={field}
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          aria-invalid={!dobValid}
        />
        {!dobValid && (
          <span className="text-sm text-[var(--coral-dark)]">Enter a valid date of birth.</span>
        )}
      </label>
      <button
        type="submit"
        disabled={!valid || submitting}
        aria-busy={submitting}
        className="nrx-cta mt-1 flex items-center justify-center gap-2.5 rounded-full px-6 py-3.5 text-base font-semibold disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
      >
        {submitting && (
          // `size-4`, NOT `w-4 h-4`: the @nrx/ui theme defines --container-1..4, which makes
          // Tailwind resolve integer `w-*` utilities to container widths (w-4 = 1136px).
          <span
            className="size-4 shrink-0 animate-spin rounded-full border-2 border-white/40 border-t-white"
            aria-hidden="true"
          />
        )}
        {submitting ? 'Checking…' : 'Check eligibility'}
      </button>
    </form>
  );
}
